import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  createBudget,
  deleteBudget,
  fetchBudgets,
  fetchCentres,
  updateBudget
} from 'api/procurement'
import constants from 'app/constants'
import { eventAlert, notify } from 'app/helpers'

import TransferDialog from './TransferDialog'

import { downloadCsv, parseCsvFile } from '../components/exportUtils'
import { PROCUREMENT_CATEGORIES, orderCentres } from '../constants'
import SopButton from '../components/SopButton'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  amountInput: { width: 130 },
  centreCell: { fontWeight: 600, minWidth: 200 }
}))

const cellKey = (centreId, category) => `${centreId}__${category}`

const Budgets = () => {
  const classes = useStyles()

  const [month, setMonth] = React.useState(Dayjs().format('YYYY-MM'))
  const [centres, setCentres] = React.useState([])
  // existing budgets keyed by `${centreId}__${category}` -> { uuid, amount }
  const [budgets, setBudgets] = React.useState({})
  // draft amounts being edited, same key -> string
  const [draft, setDraft] = React.useState({})
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [transferOpen, setTransferOpen] = React.useState(false)

  const monthDate = `${month}-01`

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const [centreRes, budgetRes] = await Promise.all([
        fetchCentres(),
        fetchBudgets({ month: monthDate })
      ])

      const centreList = orderCentres(
        Array.isArray(centreRes) ? centreRes : centreRes?.results || []
      )
      setCentres(centreList)

      const budgetList = Array.isArray(budgetRes)
        ? budgetRes
        : budgetRes?.results || []
      const map = {}
      budgetList.forEach((b) => {
        map[cellKey(b.centre, b.category)] = {
          uuid: b.uuid,
          amount: b.amount
        }
      })
      setBudgets(map)
      setDraft({})
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load budgets',
        constants.ERROR,
        'Budgets'
      )
    } finally {
      setLoading(false)
    }
  }, [monthDate])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  const getCellValue = (centreId, category) => {
    const key = cellKey(centreId, category)
    if (Object.prototype.hasOwnProperty.call(draft, key)) return draft[key]
    return budgets[key]?.amount ?? ''
  }

  const onCellChange = (centreId, category, value) => {
    // allow only numbers / blank
    if (value !== '' && Number.isNaN(Number(value))) return
    setDraft((prev) => ({ ...prev, [cellKey(centreId, category)]: value }))
  }

  const onSave = async () => {
    const changedKeys = Object.keys(draft)
    if (isEmpty(changedKeys)) {
      notify('Nothing to save', '', 'info')
      return
    }
    setSaving(true)
    try {
      for (const key of changedKeys) {
        const [centreId, category] = key.split('__')
        const existing = budgets[key]
        const raw = draft[key]
        const amount = raw === '' ? null : Number(raw)

        if (existing) {
          if (amount === null) {
            await deleteBudget(existing.uuid)
          } else if (Number(existing.amount) !== amount) {
            await updateBudget(existing.uuid, { amount })
          }
        } else if (amount !== null && amount !== 0) {
          // Below zero is a real budget: last month's overspend carried in.
          await createBudget({
            centre: centreId,
            category,
            month: monthDate,
            amount
          })
        }
      }
      notify('Budgets saved', '', 'success')
      await loadData()
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to save budgets',
        constants.ERROR,
        'Budgets'
      )
    } finally {
      setSaving(false)
    }
  }

  const buildExport = () => ({
    filename: `budgets-${month}`,
    title: `Procurement & Expenses Budgets — ${Dayjs(monthDate).format('MMMM YYYY')}`,
    headers: ['Centre', ...PROCUREMENT_CATEGORIES.map((c) => c.label)],
    rows: centres.map((c) => [
      c.name,
      ...PROCUREMENT_CATEGORIES.map((cat) => getCellValue(c.uuid, cat.key) || 0)
    ])
  })

  const downloadTemplate = () =>
    downloadCsv(
      'budgets-template',
      ['Centre', ...PROCUREMENT_CATEGORIES.map((c) => c.label)],
      centres.map((c) => [c.name, ...PROCUREMENT_CATEGORIES.map(() => '')])
    )

  const onUploadCsv = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const data = await parseCsvFile(file)
      const byName = {}
      centres.forEach((c) => {
        byName[c.name.trim().toLowerCase()] = c.uuid
      })
      const next = {}
      let matched = 0
      data.forEach((r) => {
        const cname = (r.Centre || r.centre || '').trim().toLowerCase()
        const uuid = byName[cname]
        if (!uuid) return
        matched += 1
        PROCUREMENT_CATEGORIES.forEach((cat) => {
          const val = r[cat.label] !== undefined ? r[cat.label] : r[cat.key]
          if (val !== undefined && val !== '' && !Number.isNaN(Number(val))) {
            next[cellKey(uuid, cat.key)] = String(Number(val))
          }
        })
      })
      if (!matched) {
        notify('No matching centres found in the CSV', '', 'warning')
        return
      }
      setDraft((prev) => ({ ...prev, ...next }))
      notify(
        `Loaded budgets for ${matched} centre(s). Review and Save.`,
        '',
        'success'
      )
    } catch (err) {
      eventAlert(err?.message || 'Could not read CSV', constants.ERROR, 'Budgets')
    }
  }

  const hasChanges = !isEmpty(draft)

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.toolbar}>
        <Typography variant="h6">Budgets</Typography>
        <SopButton category="hq_budgets" />
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className={classes.spacer} />
        <Button component="label" size="small" variant="outlined">
          Upload CSV
          <input type="file" hidden accept=".csv" onChange={onUploadCsv} />
        </Button>
        <Button size="small" onClick={downloadTemplate}>
          Template
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={isEmpty(centres)}
          onClick={() => setTransferOpen(true)}>
          Transfer
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!hasChanges || saving}
          onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        centres={centres}
        month={month}
        monthDate={monthDate}
        onDone={loadData}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.centreCell}>Centre</TableCell>
                {PROCUREMENT_CATEGORIES.map((c) => (
                  <TableCell key={c.key} align="right">
                    {c.label} (₹)
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {centres.map((centre) => (
                <TableRow key={centre.uuid} hover>
                  <TableCell className={classes.centreCell}>
                    {centre.name}
                  </TableCell>
                  {PROCUREMENT_CATEGORIES.map((c) => (
                    <TableCell key={c.key} align="right">
                      <TextField
                        className={classes.amountInput}
                        size="small"
                        variant="outlined"
                        type="number"
                        placeholder="0"
                        value={getCellValue(centre.uuid, c.key)}
                        onChange={(e) =>
                          onCellChange(centre.uuid, c.key, e.target.value)
                        }
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {isEmpty(centres) && (
                <TableRow>
                  <TableCell colSpan={PROCUREMENT_CATEGORIES.length + 1}>
                    <Typography variant="body2" color="textSecondary">
                      No centres found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

export default Budgets
